/**
 * BundleView — the panel body for a byte-first artifact: the packaged
 * design-skill .zip the brand-book agent delivers.
 *
 * A zip has nothing to page through the way a deck has slides, and rendering
 * its manifest on a document page (the viewer's fallback) read as a broken
 * document. What the user needs from this card is different: to see the file
 * is real, what is inside it, that it passed its gate, and one obvious way to
 * get it. The toolbar's download control is small and shared by every format;
 * the bundle is the one artifact whose entire point is being downloaded, so it
 * gets a primary button of its own.
 */
import React, { useState } from 'react';
import { Button, message as antdMessage } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { createLogger } from '@/utils/logger';
import artifactService from '@modules/conversations/services/artifactService';
import type { Artifact } from '@/types/models';

const logger = createLogger('BundleView');

interface BundleFile {
  path: string;
  bytes?: number;
}

interface BundleCheck {
  name: string;
  passed: boolean;
  detail?: string;
}

/** What package_design_skill writes as the artifact's doc_model. */
interface BundleManifest {
  file_name?: string;
  size_bytes?: number;
  brand?: { name?: string; slug?: string };
  files?: BundleFile[];
  audit?: { passed?: number; total?: number; checks?: BundleCheck[] };
}

const kb = (bytes: number) => Math.max(1, Math.round(bytes / 1024));

const BundleView: React.FC<{ artifact: Artifact }> = ({ artifact }) => {
  const { t } = useLanguage();
  const manifest = (artifact.doc_model || {}) as BundleManifest;
  const [busy, setBusy] = useState(false);
  const files = manifest.files || [];
  const fileName = manifest.file_name || `${artifact.title || 'design-skill'}.zip`;

  // Group by directory so the tree reads the way the zip unpacks.
  const groups = files.reduce<Record<string, BundleFile[]>>((acc, f) => {
    const dir = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : '';
    (acc[dir] ||= []).push(f);
    return acc;
  }, {});

  const download = async () => {
    setBusy(true);
    try {
      await artifactService.download(artifact.artifact_id, fileName);
    } catch (err) {
      logger.error('Bundle download failed', err);
      antdMessage.error(t('chat.artifacts.downloadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const audit = manifest.audit;

  return (
    <div className="artifact-bundle" data-testid="artifact-bundle">
      <header className="artifact-bundle__head">
        <div className="artifact-bundle__eyebrow">{t('chat.artifacts.bundle.title')}</div>
        {/* The bundle's own file name, the check details and every path below
            are DATA, not UI prose: a Hebrew tenant's bundle carries Hebrew
            names and an English one sits inside a Hebrew page. dir="auto"
            goes on the leaf that holds the text, never on a flex container -
            there it would reorder the children too. */}
        <h3 className="artifact-bundle__name" dir="auto">{fileName}</h3>
        <div className="artifact-bundle__meta">
          {manifest.brand?.name && (
            <span>{t('chat.artifacts.bundle.brand')}: <span dir="auto">{manifest.brand.name}</span></span>
          )}
          <span>{t('chat.artifacts.bundle.filesCount', { count: files.length })}</span>
          {manifest.size_bytes != null && (
            <span>{t('chat.artifacts.bundle.size', { kb: kb(manifest.size_bytes) })}</span>
          )}
        </div>
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          loading={busy}
          onClick={download}
          data-testid="artifact-bundle-download"
        >
          {t('chat.artifacts.bundle.download', { name: fileName })}
        </Button>
        <p className="artifact-bundle__install">{t('chat.artifacts.bundle.install')}</p>
      </header>

      {audit && (
        <section className="artifact-bundle__audit" data-testid="artifact-bundle-audit">
          <h4>
            {t('chat.artifacts.bundle.audit', {
              passed: audit.passed ?? 0,
              total: audit.total ?? 0,
            })}
          </h4>
          <ul>
            {(audit.checks || []).map(c => (
              <li key={c.name} className={c.passed ? 'is-pass' : 'is-fail'}>
                <span className="artifact-bundle__check-name" dir="auto">{c.name}</span>
                {c.detail && <span className="artifact-bundle__check-detail" dir="auto">{c.detail}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="artifact-bundle__files">
        <h4>{t('chat.artifacts.bundle.contents')}</h4>
        {Object.entries(groups).map(([dir, list]) => (
          <div key={dir || '.'} className="artifact-bundle__group">
            {dir && <div className="artifact-bundle__dir" dir="auto">{dir}/</div>}
            <ul>
              {list.map(f => (
                <li key={f.path}>
                  <code dir="auto">{f.path.slice(f.path.lastIndexOf('/') + 1)}</code>
                  {f.bytes != null && (
                    <span className="artifact-bundle__bytes">
                      {t('chat.artifacts.bundle.size', { kb: kb(f.bytes) })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  );
};

export default BundleView;
